﻿import { useState, useEffect, useRef } from "react";
import introJs from "intro.js";
import { useDispatch, useSelector } from "react-redux";
import { useToasts } from "react-toast-notifications";
import { Row, Col, Table } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";

import classes from "./inventory.module.scss";

import Head from "@/components/Content/Head";
import Fetching from "@/components/Fetching/Fetching";
import BasicConfirm from "@/components/Confirm/BasicConfirm";
import FormPrint from "./Print/FormPrint";
import InventoryTable from "./InventoryTable";
import ModalFetching from "@/components/Fetching/ModalFetching";
import ButtonFilter from "@/components/Filter/ButtonFilter";
import BasePagination from "@/components/Pagination/BasePagination";
import CodesMovements from "@/components/Product/ProductMovements/CodesMovements";
import ProductCriticalStock from "@/components/Product/ProductCriticalStock/ProductCriticalStock";

import {
	FETCH_INVENTORY_SUCCESS,
	INITIAL_INVENTORY_REQUEST,
	CHANGE_SELECT_INVENTORIES,
	CHANGE_TAGS_AVAILABLE,
	FINAL_INVENTORY_REQUEST,
} from "@/redux/types/inventory.types";
import { FETCHING_ADD_DOWNLOAD } from "@/redux/types/main.types";
import { fetchInventories } from "@/redux/actions/inventory.actions";

import { dateFormat, dateFormatYmd, getToday, hasPermission } from "@/services/Utils";
import api from "@/services/api.service";

import { baseFilters } from "./inventory.constants";
import InventoryFunctions from "./InventoryFunctions";
import InventoryFunctionsSelect from "./InventoryFunctionsSelect";
import InventoryFilter from "./InventoryFilter";
import InventoryExcelDate from "./InventoryExcelDate";

import InventoryMobile from "./Mobile/InventoryMobile";
import ProductPending from "@/components/Product/ProductPending/ProductPending";
import InventoryHead from "./InventoryHead";

const steps = [
	{
		element: "#printerLableBtn",
		intro: "Aqui podras ver las etiquetas por imprimir cargadas en el inventario inicial",
	},
];

const Inventory = () => {
	const { addToast } = useToasts();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const introInstance = useRef(null);

	const { quantity, from, to, last_page, isFetching, message, selected, permissions } = useSelector((state) => state.inventory);

	const { filter } = useSelector((state) => state.auth);
	const { estate_parent_id, estate_id, type_currency, search, parameter } = filter;

	const [base, setBase] = useState({
		...baseFilters,
	});

	const [typePrint, setTypePrint] = useState("new");

	const [titles, setTitles] = useState([]);
	const [dateExcel, setDateExcel] = useState("");

	const [showFilter, setShowFilter] = useState(false);

	const [showConfirmDelete, setShowConfirmDelete] = useState(false);
	const [showConfirmStolen, setShowConfirmStolen] = useState(false);
	const [showDateExcel, setShowDateExcel] = useState(false);
	const [showPrint, setShowPrint] = useState(false);

	useEffect(() => {
		const params = {
			...base,
			estate_parent_id,
			estate_id,
			type_currency,
			search,
			parameter,
		};

		dispatch(fetchInventories(params)).then((response) => {
			setTitles(response.titles);
		});

		// eslint-disable-next-line
	}, [base, estate_parent_id, estate_id, type_currency, search, parameter]);

	useEffect(() => {
		if (!isFetching && location.state?.from === "tour") {
			const startTour = () => {
				introInstance.current = introJs();
				introInstance.current.setOptions({
					steps: steps,
					exitOnOverlayClick: false,
					exitOnEsc: false,
					showProgress: true,
					showBullets: true,
					prevLabel: "Anterior",
					nextLabel: "Siguiente",
					doneLabel: "Ir al inventario",
				});
				introInstance.current.start();

				introInstance.current.oncomplete(() => {
					navigate({
						pathname: "/inventario",
						state: { from: "finishTour" },
					});
				});
			};

			setTimeout(() => {
				startTour();
			}, 1000);
		}
		// eslint-disable-next-line
	}, [isFetching, location.state]);

	const applyFilter = (params) => {
		let newBase = { ...base };
		newBase.page = 1;

		if (params.defaultUnitFilter !== base.defaultUnit) {
			newBase.defaultUnit = params.defaultUnitFilter;
		}

		if (params.dateFilter !== base.createdDate) {
			newBase.createdDate = params.dateFilter;
		}

		if (params.dateToFilter !== base.createdDateTo) {
			newBase.createdDateTo = params.dateToFilter;
		}

		if (params.aptitudeFilter !== base.aptitudeID) {
			newBase.aptitudeID = params.aptitudeFilter;
		}

		if (params.casFilter !== base.cas) {
			newBase.cas = params.casFilter;
		}

		if (params.formulaFilter !== base.formula) {
			newBase.formula = params.formulaFilter;
		}

		if (params.showUsageFilter !== base.showUsage) {
			newBase.showUsage = params.showUsageFilter;
		}

		if (params.showGenericFilter !== base.showGeneric) {
			newBase.showGeneric = params.showGenericFilter;
		}

		if (params.showInitiallyLoadedFilter !== base.showInitiallyLoaded) {
			newBase.showInitiallyLoaded = params.showInitiallyLoadedFilter;
		}

		if (params.businessFilter !== base.business) {
			newBase.business = params.businessFilter;
		}

		if (params.expiredFilter !== base.expired) {
			newBase.expired = params.expiredFilter;
		}

		setBase(newBase);
		setShowFilter(false);
	};

	const handleShowPrint = () => {
		setShowPrint(true);
	};

	const randomInventory = () => {
		setBase({ ...base, random: !base.random, page: 1 });
	};

	const exportExcel = (option) => {
		const filename = `Inventario ${getToday()}.xlsx`;

		dispatch({
			type: FETCHING_ADD_DOWNLOAD,
			payload: {
				file: {
					key: Array.from({ length: 25 }, () => Math.random().toString(36)[2] || "0").join(""),
					params: { ...base, estate_parent_id, estate_id, type_currency, search, parameter, option: option ? option : "" },
					show: true,
					url: "/client/inventories/export_excel",
					name: filename,
					type: "excel",
					inExecution: false,
					inProgress: false,
					inChecked: false,
				},
			},
		});
	};

	const exportOldExcel = () => {
		const filename = `Inventario Historico ${dateFormat(dateFormatYmd(dateExcel))}.xlsx`;

		dispatch({
			type: FETCHING_ADD_DOWNLOAD,
			payload: {
				file: {
					key: Array.from({ length: 25 }, () => Math.random().toString(36)[2] || "0").join(""),
					params: {
						...base,
						estate_parent_id,
						estate_id,
						type_currency,
						search,
						parameter,
						dateExcel: dateFormat(dateFormatYmd(dateExcel)),
					},
					show: true,
					url: "/client/inventories/export_old_excel",
					name: filename,
					type: "excel",
					inExecution: false,
					inProgress: false,
					inChecked: false,
				},
			},
		});
	};

	const handleGlobalStolen = () => {
		setShowConfirmStolen(true);
	};

	const handleGlobalDelete = () => {
		setShowConfirmDelete(true);
	};

	const confirmGlobalDelete = () => {
		setShowConfirmDelete(false);

		dispatch({
			type: INITIAL_INVENTORY_REQUEST,
			payload: {
				message: "Eliminando registros ...",
			},
		});

		api.post("/client/inventories/delete_inventories", {
			...base,
			estate_parent_id,
			estate_id,
			type_currency,
			search,
			parameter,
			inventories: selected,
		}).then((response) => {
			if (response?.data && !Object.prototype.hasOwnProperty.call(response.data, "inventories")) {
				dispatch({
					type: CHANGE_SELECT_INVENTORIES,
					payload: {
						selected: [],
					},
				});

				addToast("Productos eliminados correctamente del inventario.", {
					appearance: "success",
				});

				dispatch({
					type: FETCH_INVENTORY_SUCCESS,
					payload: {
						inventories: response.data.inventories.data,
						aptitudes: response.data.aptitudes,
						active_ingredients: response.data.active_ingredients,
						cas: response.data.cas,
						formulas: response.data.formulas,
						products: response.data.products,
						dealers: response.data.dealers,
						quantity: response.data.inventories.total,
						last_page: response.data.inventories.last_page,
						from: response.data.inventories.from,
						to: response.data.inventories.to,
						permissions: response.data.permissions,
					},
				});

				dispatch({
					type: CHANGE_TAGS_AVAILABLE,
					payload: {
						tags_available: response.data.tags_available,
						lots_available: response.data.lots_available,
					},
				});
			} else {
				dispatch({
					type: FINAL_INVENTORY_REQUEST,
				});
			}
		});
	};

	const confirmGlobalStolen = () => {
		setShowConfirmStolen(false);

		dispatch({
			type: INITIAL_INVENTORY_REQUEST,
			payload: {
				message: "Marcando productos como robados ...",
			},
		});

		api.post("/client/inventories/stolen_inventories", { ...base, inventories: selected }).then((response) => {
			if (response.data && Object.prototype.hasOwnProperty.call(response.data, "inventories")) {
				addToast("Productos marcados como borrados.", {
					appearance: "success",
				});

				dispatch({
					type: CHANGE_SELECT_INVENTORIES,
					payload: {
						selected: [],
					},
				});

				dispatch({
					type: FETCH_INVENTORY_SUCCESS,
					payload: {
						inventories: response.data.inventories.data,
						aptitudes: response.data.aptitudes,
						active_ingredients: response.data.active_ingredients,
						cas: response.data.cas,
						formulas: response.data.formulas,
						products: response.data.products,
						dealers: response.data.dealers,
						quantity: response.data.inventories.total,
						last_page: response.data.inventories.last_page,
						from: response.data.inventories.from,
						to: response.data.inventories.to,
						permissions: response.data.permissions,
					},
				});

				dispatch({
					type: CHANGE_TAGS_AVAILABLE,
					payload: {
						tags_available: response.data.tags_available,
						lots_available: response.data.lots_available,
					},
				});
			} else {
				dispatch({
					type: FINAL_INVENTORY_REQUEST,
				});
			}
		});
	};

	if (isFetching && message === "") {
		return <Fetching />;
	}

	return (
		<>
			<Head title={"Inventario"} titles={titles} />

			<ButtonFilter origin="main" size_md={12} showFilter={showFilter} setShowFilter={setShowFilter} show_back={true} />

			<ProductPending />

			<Row className={`no_print ${classes.inventoryGlobal}`}>
				{showFilter && <InventoryFilter base={base} applyFilter={applyFilter} />}
				<Col md={showFilter ? 9 : 12} className={classes.inventory}>
					<InventoryFunctions
						setTypePrint={setTypePrint}
						handleShow={handleShowPrint}
						random={base.random}
						randomInventory={randomInventory}
						exportExcel={exportExcel}
						showFilter={showFilter}
						setShowFilter={setShowFilter}
						setShowDateExcel={setShowDateExcel}
						locationFrom={location.state?.from}
					/>

					{selected.filter((s) => s.type !== "simple_delete").length > 0 && (
						<InventoryFunctionsSelect
							handleShow={handleShowPrint}
							setTypePrint={setTypePrint}
							handleGlobalDelete={handleGlobalDelete}
							handleGlobalStolen={handleGlobalStolen}
						/>
					)}

					{/* TABLE */}
					<Row className={`${classes.table} ${selected.length > 0 && classes.table_min}`}>
						{hasPermission("show", permissions) && (
							<>
								<Table id="inventory-list" className={classes.table_desktop}>
									<InventoryHead base={base} setBase={setBase} />
									<InventoryTable base={base} />
								</Table>
								<InventoryMobile />
							</>
						)}
					</Row>

					<BasePagination
						page={base.page}
						paginate={base.paginate}
						last_page={last_page}
						quantity={quantity}
						from={from}
						to={to}
						setPage={(page) => setBase({ ...base, page })}
						setPaginate={(paginate) => setBase({ ...base, paginate })}
					/>
				</Col>
			</Row>

			<FormPrint
				showPrint={showPrint}
				setShowPrint={setShowPrint}
				page={base.page}
				paginate={base.paginate}
				defaultUnit={base.defaultUnit}
				typePrint={typePrint}
				typeCurrency={type_currency}
			/>

			<BasicConfirm
				showConfirm={showConfirmDelete}
				setShowConfirm={setShowConfirmDelete}
				handleAction={confirmGlobalDelete}
				handleActionCancel={() => {
					dispatch({ type: CHANGE_SELECT_INVENTORIES, payload: { selected: [] } });
					setShowConfirmDelete(false);
				}}
				showValidation={true}
				subMessage={
					<div>
						<div>{`Vas a eliminar ${selected.length} productos, estos códigos se eliminarán completamente. Estás seguro que deseas continuar?`}</div>
						<br />
						<span style={{ fontWeight: 500, color: "red" }}>
							Si el producto se encuentra asociado a una factura o guía de despacho, se eliminará el registro de los documentos.
						</span>
					</div>
				}
				textValidation={"eliminar productos seleccionados"}
			/>

			<BasicConfirm
				showConfirm={showConfirmStolen}
				setShowConfirm={setShowConfirmStolen}
				handleAction={confirmGlobalStolen}
				showValidation={true}
				subMessage={`Vas a marcar como robados ${selected.length} productos, estos códigos se registrarán en el historial de movimientos. Estás seguro que deseas continuar?`}
				textValidation={"marcar como robados"}
			/>

			<ModalFetching loading={isFetching} text={message} />

			<InventoryExcelDate
				showModal={showDateExcel}
				setShowModal={setShowDateExcel}
				handleAction={exportOldExcel}
				dateExcel={dateExcel}
				setDateExcel={setDateExcel}
			/>

			<CodesMovements type={"Inventory"} />

			<ProductCriticalStock />
		</>
	);
};

export default Inventory;
